import type { MaybeRef } from 'vue';
import { AppStore } from '@pkg/sanity-utils/constants/appstore';
import { sanityImageWithAsset } from '@pkg/sanity-utils/sanityImageWithAsset';
import { Social } from '@pkg/sanity-utils/constants/social';
import {
  buildPageJsonLdGraph,
  type ContactPointConfig,
  type JsonLdPageType,
} from './buildPageJsonLdGraph';
import {
  injectJsonLdScript,
  useJsonLdShared,
  type PageSeoData,
} from './useJsonLdShared';
import { useSiteManifestStore } from '~/app/stores/siteManifest';

export type { ContactPointConfig, JsonLdPageType } from './buildPageJsonLdGraph';
export {
  buildPageJsonLdGraph,
  resolvePageSchemaType,
} from './buildPageJsonLdGraph';

export interface PageJsonLdConfig {
  pageData: PageSeoData;
  /** Function to check if current route is the target page */
  isCurrentPage: (path: string) => boolean;
  /** Schema.org page type: WebPage, AboutPage, or ContactPage */
  pageType: JsonLdPageType;
  /** Default title when page.seoSocial.metaTitle is missing */
  defaultTitle: string;
  /** Path for the page (e.g. '/' or '/about-us' or '/contact-us') */
  pagePath: string;
  /** Unique key for useHead script injection */
  scriptKey: string;
  /** When true, page and website URLs use homepage trailing-slash logic */
  isHomePage?: boolean;
  /** For ContactPage: mainEntity ContactPoint (email, url, contactType) */
  contactPoint?: MaybeRef<ContactPointConfig | null>;
}

/**
 * Shared composable that builds and injects JSON-LD structured data for a page.
 * Used by useHomeJsonLd, useAboutUsJsonLd, and useContactUsJsonLd.
 */
export function usePageJsonLd(config: PageJsonLdConfig) {
  const { route, localeValue, localePath, baseUrl, seoFromPageData } =
    useJsonLdShared();
  const { $urlFor } = useNuxtApp();
  const siteManifestStore = useSiteManifestStore();

  const shouldRender = computed(() => config.isCurrentPage(route.path));

  const jsonLd = computed(() => {
    if (!shouldRender.value) {
      return null;
    }

    const siteBase = baseUrl.value;
    const locale = localeValue.value;
    const { name: pageTitle, description: pageDescription } =
      seoFromPageData(config.pageData, config.defaultTitle);

    let websiteUrl: string;
    let pageUrl: string;

    if (config.isHomePage) {
      const pathSegment = localePath('/').replace(/^\//, '') || locale;
      const homepagePath = pathSegment ? `/${pathSegment}` : '/';
      pageUrl = websiteUrl =
        homepagePath === '/' ? `${siteBase}/` : `${siteBase}${homepagePath}/`;
    } else {
      websiteUrl = `${siteBase}${localePath('/')}`;
      pageUrl = `${siteBase}${localePath(config.pagePath)}`;
    }

    const defaultSeo = siteManifestStore.defaultSeo;
    const appStores = siteManifestStore.appStores;
    const social = siteManifestStore.social;
    const appDescription = (
      defaultSeo?.mobileAppDescription ||
      defaultSeo?.mobileAppDescriptionFallback ||
      ''
    ).trim();

    const favicon = sanityImageWithAsset(defaultSeo?.favicon);
    const logoUrl = favicon
      ? $urlFor(favicon).width(512).height(512).url()
      : `${siteBase.replace(/\/$/, '')}/images/logo.svg`;

    const sameAs = [
      social?.[Social.LINKEDIN],
      social?.[Social.INSTAGRAM],
      social?.[Social.FACEBOOK],
      social?.[Social.TWITTER],
      social?.[Social.YOUTUBE],
      social?.[Social.TIKTOK],
    ].filter(Boolean) as string[];

    const contactPoint = config.pageType === 'ContactPage' ? unref(config.contactPoint) : null;

    return buildPageJsonLdGraph({
      siteBase,
      locale,
      pageType: config.pageType,
      pageTitle,
      pageDescription,
      websiteUrl,
      pageUrl,
      logoUrl,
      sameAs,
      appleStoreUrl: appStores?.[AppStore.APPLE_APP_STORE] ?? '',
      playStoreUrl: appStores?.[AppStore.GOOGLE_PLAY_STORE] ?? '',
      appDescription,
      iosRatingValue: defaultSeo?.iosRatingValue,
      iosRatingCount: defaultSeo?.iosRatingCount,
      androidRatingValue: defaultSeo?.androidRatingValue,
      androidRatingCount: defaultSeo?.androidRatingCount,
      contactPoint,
    });
  });

  injectJsonLdScript(config.scriptKey, jsonLd);
}
